// 18c — the supply side of Insights.
//
// The artboard for this screen was drawn on the assumption that search queries
// are not logged, and its two panels are what remains when you cannot ask "what
// did people want and not get". In THIS deployment queries ARE logged
// (`kb_search_logs`, plus the gap endpoints the Insights tab already uses), so
// the demand-side gap queue stays exactly where it is and these panels join it
// rather than replacing it. They answer a different question:
//
//   Gap queue  — what people asked for and did not get.  (demand)
//   These two  — what we wrote that nobody wants, and where we promised a
//                shelf and left it bare.                 (supply)
//
// Both are derived from records that already exist, so neither needs a
// migration and neither can drift out of step with the register.

import type { Category } from '@services/knowledge.service';
import { isStale, type ArticleRow } from './types';

export interface EmptyCategory {
  id: string;
  name: string;
  slug: string;
  /** 'UNDER BILLING', or null at the root. */
  parentName: string | null;
}

/**
 * Categories somebody created and nothing fills.
 *
 * Inactive categories are excluded: they are hidden everywhere, so an empty
 * one is not an unkept promise — it is a shelf that was deliberately taken
 * down, and listing it as work to do wastes the reader's attention on the one
 * panel whose whole value is that every row is worth acting on.
 */
export function emptyCategories(tree: Category[]): EmptyCategory[] {
  const out: EmptyCategory[] = [];

  const walk = (nodes: Category[], parentName: string | null) => {
    for (const node of nodes) {
      const active = node.isActive !== false;
      if (active && (node.articleCount ?? 0) === 0) {
        out.push({
          id: node.id,
          name: node.name,
          slug: node.slug,
          parentName,
        });
      }
      if (node.children?.length) walk(node.children, node.name);
    }
  };

  walk(tree, null);
  return out;
}

/**
 * The least-read articles — the bottom of the register by views.
 *
 * Ties break towards the older article, because two articles with one view
 * each are not equally interesting: the one nobody has revisited in four
 * months is the stronger candidate for a re-title or a deletion.
 *
 * Only PUBLISHED articles qualify. A draft nobody reads is not a finding; it
 * is a draft.
 */
export function leastRead(rows: ArticleRow[], limit = 5): ArticleRow[] {
  return rows
    .filter((r) => r.status === 'published')
    .slice()
    .sort(
      (a, b) =>
        a.views - b.views ||
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    )
    .slice(0, limit);
}

export interface CoverageStat {
  label: string;
  value: string;
  /** Drawn in warn ink — a number that is a problem, not just a figure. */
  warn?: boolean;
  /** Why this number is what it is, when the label alone would mislead. */
  title?: string;
}

/**
 * The figures above the panels — only the ones nothing else on this screen has.
 *
 * Article and view totals used to be here too. They are in the dashboard's
 * Content Overview panel directly above, and a figure printed twice on one
 * screen makes people check whether the copies agree instead of reading
 * either. These two are computed from the register rather than the analytics
 * payload, which is why they have nowhere else to live.
 */
export function coverageStats(
  rows: ArticleRow[],
  empties: EmptyCategory[],
  categoryCount: number,
): CoverageStat[] {
  const unreviewed = rows.filter((r) => isStale(r.updatedAt)).length;

  return [
    {
      label: 'Categories with nothing in them',
      value: String(empties.length),
      warn: empties.length > 0,
      title: `${categoryCount} categories exist`,
    },
    {
      label: 'Unreviewed over 60d',
      value: String(unreviewed),
      warn: unreviewed > 0,
      title: `Of ${rows.length} articles loaded. Nothing here is wrong yet — but nobody has checked.`,
    },
  ];
}
